import React, { useMemo } from "react";
import { Col, DatePicker, Divider, Form, Modal, Row, Select, Spin } from "antd";
import type { Moment } from "moment";
import type { FormInstance } from "antd/es/form/Form";
import NumberFormat from "react-number-format";
import { useAppSelector } from "../../../..";
import { ReactComponent as CancelButtonIcon } from "../../../../img/buttons_icons/cancelButton_icon.svg";
import { ReactComponent as SubmitButtonIcon } from "../../../../img/buttons_icons/submitButton_icon.svg";
import { checkIsLoading, displayFormat, getSelectOptions } from "../../../../lib/utils";
import type {
  ICreateMode,
  IServiceProfileFieldName,
  IViewMode,
  Offer,
} from "../../../../lib/types";
import { isFieldAvailable } from "./utils/isFieldAvailable";
import { isFieldRequired } from "./utils/isFieldRequired";
import { OFFER_STATUSES } from "../../../../store/slices/offering/const";
import { IOfferServiceProfileFormData } from "../../../../containers/OfferContainer/context/types";
import "./ProductServiceProfileEdit.less";

const formItemLayout = {
  labelCol: { span: 10 },
  wrapperCol: { span: 14 },
  className: "form-item-required__hidden-error__mb0",
};

const rowLayout = {
  gutter: 90,
  style: {
    marginTop: "10px",
  },
};

interface ComponentProps {
  clearServiceProfile: () => void;
  viewMode: Exclude<IViewMode, "view"> | ICreateMode;
  onCancel?: () => void;
  onSubmit?: () => void;
  offeringStatusId?: number;
  offer: Offer<Moment, "createForm">;
  serviceProfileForm: FormInstance<IOfferServiceProfileFormData>;
  editingAllowed: boolean;
}

/* ------------------------------------------------------ Начало компонента ------------------------------------------------------ */

export const ProductServiceProfileEdit = (props: ComponentProps) => {
  const {
    clearServiceProfile,
    viewMode,
    onCancel,
    onSubmit,
    offer,
    serviceProfileForm,
    editingAllowed,
  } = props;
  const { billPeriods, billPeriodsLoading } = useAppSelector((state) => state.dicts);
  const {
    paymentType,
    paymentTypeLoading,
    profileTypeStatus: profileTypes,
    profileTypeStatusLoading: profileTypesLoading,
    invoicePeriodStatus: invoicePeriods,
    invoicePeriodStatusLoading: invoicePeriodsLoading,
    prePaymentPeriodStatus: prepaymentPeriods,
    prePaymentPeriodStatusLoading: prepaymentPeriodsLoading,
  } = useAppSelector((state) => state.rtkDictionaries);
  const {
    serviceProfiles: commonServiceProfiles,
    serviceProfilesLoading: commonServiceProfilesLoading,
  } = useAppSelector((state) => state.serviceProfile);

  const { serviceProfileLoading } = useAppSelector((state) => state.offering);

  const handleOnCancel = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();
    onCancel && onCancel();
  };

  const handleOnSubmit = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();
    onSubmit && onSubmit();
  };

  // Для типа тарифа пакетный, доступны только профили с invoicePeriodId: 1
  const filteredСommonServiceProfiles = useMemo(() => {
    if (offer?.tariffModelId === 2) {
      return commonServiceProfiles.filter(({ invoicePeriodId }) => invoicePeriodId === 1);
    }
    return commonServiceProfiles;
  }, [commonServiceProfiles, offer]);

  const onSelectId = (v, data) => {
    Modal.info({
      content: "Профиль изменен на индивидуальный",
      okButtonProps: {
        style: {
          width: "108px",
        },
      },
    });
    const { profileTypeId, fromDate, toDate, ...fieldsValues } = data.option;
    serviceProfileForm.setFieldsValue(fieldsValues);
  };

  const isCommonType = useMemo(() => {
    if (viewMode === "create") {
      return offer.offeringTypeId === 1;
    }
    return offer.serviceProfile.profileTypeId === 1;
  }, [viewMode, offer]);

  const offeringStatusId = useMemo(() => {
    if (viewMode === "create") return OFFER_STATUSES.PREPARE;
    return offer?.offeringStatusId;
  }, [viewMode, offer]);

  const getRequired = (fieldName: IServiceProfileFieldName): boolean => {
    if (!editingAllowed) {
      return false;
    }
    if (offeringStatusId) {
      return isFieldRequired({
        fieldName,
        viewMode,
        offeringStatusId,
        options: { isCommonType },
      });
    }
    return false;
  };

  const getAvailable = (fieldName: IServiceProfileFieldName): boolean => {
    // console.log({
    //   fieldName,
    //   viewMode,
    //   offeringStatusId,
    //   options: { isCommonType },
    // });
    if (!editingAllowed) {
      return false;
    }
    if (offeringStatusId) {
      return isFieldAvailable({
        fieldName,
        viewMode,
        offeringStatusId,
        options: { isCommonType },
      });
    }
    return false;
  };

  const disableFromDate = (date: Moment): boolean =>
    !date.isBetween(offer.serviceProfile?.fromDate, offer.serviceProfile?.toDate, "day", "(]");

  const renderButtons = () =>
    viewMode !== "create" ? (
      <Row align="middle" className="productServiceProfile-edit__btns">
        <Col>
          <SubmitButtonIcon onClick={handleOnSubmit} />
        </Col>
        <Col>
          <CancelButtonIcon onClick={handleOnCancel} />
        </Col>
      </Row>
    ) : null;

  return (
    <div className="productServiceProfile-edit">
      <Divider style={{ margin: "0 0 9px" }} />
      <Spin spinning={checkIsLoading(serviceProfileLoading)}>
        {renderButtons()}
        <Row {...rowLayout}>
          <Col span={10}>
            <Form.Item
              label="Профиль"
              name="id"
              {...formItemLayout}
              rules={[{ required: getRequired("id"), message: "" }]}
            >
              <Select
                style={isCommonType ? {} : { color: "white" }}
                options={getSelectOptions({
                  list: filteredСommonServiceProfiles,
                })}
                allowClear
                loading={checkIsLoading(commonServiceProfilesLoading)}
                onSelect={onSelectId}
                onClear={clearServiceProfile}
                disabled={!getAvailable("id")}
              />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item
              label="Тип профиля"
              name="profileTypeId"
              {...formItemLayout}
              rules={[{ required: getRequired("profileTypeId"), message: "" }]}
            >
              <Select
                disabled={!getAvailable("profileTypeId")}
                showArrow={false}
                options={getSelectOptions({
                  list: profileTypes,
                })}
                loading={checkIsLoading(profileTypesLoading)}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row {...rowLayout}>
          <Col span={10}>
            <Form.Item
              label="Тип оплаты"
              name="paymentTypeId"
              {...formItemLayout}
              rules={[{ required: getRequired("paymentTypeId"), message: "" }]}
            >
              <Select
                disabled={!getAvailable("paymentTypeId")}
                showArrow={getAvailable("paymentTypeId")}
                options={getSelectOptions({
                  list: paymentType,
                })}
                allowClear
                loading={checkIsLoading(paymentTypeLoading)}
              />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item
              label="Срок на предоплату (дн)"
              name="prepaymentToPayDays"
              {...formItemLayout}
              rules={[
                { required: getRequired("prepaymentToPayDays"), message: "" },
                {
                  validator: (rule, value) => {
                    if (Number(value) < 1) {
                      return Promise.reject();
                    }
                    return Promise.resolve();
                  },
                  message: "",
                },
              ]}
            >
              <NumberFormat
                className="ant-input-affix-wrapper inputNumber__hideArrows"
                decimalScale={0}
                fixedDecimalScale
                allowNegative={false}
                disabled={!getAvailable("prepaymentToPayDays")}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row {...rowLayout}>
          <Col span={10}>
            <Form.Item
              label="Расчетный период"
              name="invoicePeriodId"
              {...formItemLayout}
              rules={[{ required: getRequired("invoicePeriodId"), message: "" }]}
            >
              <Select
                disabled={!getAvailable("prepaymentToPayDays")}
                showArrow={getAvailable("prepaymentToPayDays")}
                options={getSelectOptions({
                  // list: invoicePeriods.filter(({ id }) => id === 1),
                  list: invoicePeriods,
                })}
                allowClear
                loading={checkIsLoading(invoicePeriodsLoading)}
              />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item
              label="Срок на постоплату (дн)"
              name="billToPayDays"
              {...formItemLayout}
              rules={[
                { required: getRequired("billToPayDays"), message: "" },
                {
                  validator: (rule, value) => {
                    if (Number(value) < 1) {
                      return Promise.reject();
                    }
                    return Promise.resolve();
                  },
                  message: "",
                },
              ]}
            >
              <NumberFormat
                className="ant-input-affix-wrapper inputNumber__hideArrows"
                decimalScale={0}
                fixedDecimalScale
                allowNegative={false}
                disabled={!getAvailable("billToPayDays")}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row {...rowLayout}>
          <Col span={10}>
            <Form.Item
              label="Период счета на предоплату"
              name="prepaymentPeriodId"
              {...formItemLayout}
              rules={[{ required: getRequired("prepaymentPeriodId"), message: "" }]}
            >
              <Select
                disabled={!getAvailable("prepaymentPeriodId")}
                showArrow={getAvailable("prepaymentPeriodId")}
                options={getSelectOptions({
                  list: prepaymentPeriods,
                })}
                allowClear
                loading={checkIsLoading(prepaymentPeriodsLoading)}
              />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item
              label="Период счета на постоплату"
              name="billPeriodId"
              {...formItemLayout}
              rules={[{ required: getRequired("billPeriodId"), message: "" }]}
            >
              <Select
                disabled={!getAvailable("billPeriodId")}
                showArrow={getAvailable("billPeriodId")}
                options={getSelectOptions({
                  list: billPeriods,
                })}
                allowClear
                loading={checkIsLoading(billPeriodsLoading)}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row {...rowLayout}>
          {viewMode !== "create" && (
            <Col span={10} offset={10}>
              <Row>
                <Col span={formItemLayout.labelCol.span}>
                  <Form.Item
                    // name="fromDate"
                    label="Период"
                    className="form-item-required__hidden-error__mb0"
                    rules={[{ required: getRequired("fromDate"), message: "" }]}
                  >
                    <></>
                  </Form.Item>
                </Col>
                <Col span={formItemLayout.wrapperCol.span / 2 - 1}>
                  <Form.Item
                    name="fromDate"
                    className="form-item-required__hidden-error__mb0"
                    rules={[{ required: getRequired("fromDate"), message: "" }]}
                  >
                    <DatePicker
                      format={displayFormat}
                      disabledDate={disableFromDate}
                      disabled={!getAvailable("fromDate")}
                    />
                  </Form.Item>
                </Col>
                <Col span={2}>
                  <div className="tarification-params-edit__periodSeparator">-</div>
                </Col>
                <Col span={formItemLayout.wrapperCol.span / 2 - 1}>
                  <Form.Item
                    name="toDate"
                    className="form-item-required__hidden-error__mb0"
                    rules={[{ required: getRequired("toDate"), message: "" }]}
                  >
                    <DatePicker
                      format={displayFormat}
                      disabledDate={disableFromDate}
                      disabled={!getAvailable("toDate")}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          )}
        </Row>
      </Spin>
    </div>
  );
};
